import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Brain, Download, Trash2, Search, PauseCircle, PlayCircle, RefreshCw, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  preference: "Preferences",
  brand: "Brand details",
  language: "Language",
  style: "Style & tone",
  workflow: "Workflows",
  activity: "Activity",
  instruction: "Instructions",
};

const CAT_COLORS = {
  preference: "rgb(var(--ac-cyan))",
  brand: "rgb(var(--ac-pink))",
  language: "rgb(var(--ac-blue))",
  style: "rgb(var(--ac-yellow))",
  workflow: "rgb(var(--ac-orange))",
  activity: "rgb(var(--ac-teal))",
  instruction: "rgb(var(--ac-purple))",
};

export default function MemorySettings() {
  const [settings, setSettings] = useState(null);
  const [facts, setFacts] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [scope, setScope] = useState("all");
  const [busy, setBusy] = useState(false);
  const [confirmNuke, setConfirmNuke] = useState(false);

  const loadSettings = useCallback(async () => {
    const r = await api.get("/memory/settings");
    setSettings(r.data);
  }, []);

  const loadFacts = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (category !== "all") params.set("category", category);
    if (scope !== "all") params.set("scope", scope);
    params.set("limit", "300");
    const r = await api.get(`/memory/facts?${params.toString()}`);
    setFacts(r.data?.facts || []);
  }, [q, category, scope]);

  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadFacts(); }, [loadFacts]);

  const patch = async (body) => {
    setBusy(true);
    try {
      const r = await api.patch("/memory/settings", body);
      setSettings(r.data);
    } catch { toast.error("Update failed"); } finally { setBusy(false); }
  };

  const toggleCategory = (k, v) => patch({ categories: { [k]: v } });

  const removeFact = async (id) => {
    try {
      await api.delete(`/memory/facts/${id}`);
      setFacts((fs) => fs.filter((f) => f.fact_id !== id));
      toast.success("Forgotten");
    } catch { toast.error("Could not delete"); }
  };

  const nukeAll = async () => {
    setBusy(true);
    try {
      const r = await api.delete("/memory/all");
      toast.success(`Deleted ${r.data.deleted} memories`);
      setFacts([]);
      setConfirmNuke(false);
    } finally { setBusy(false); }
  };

  const exportMemory = async () => {
    try {
      const r = await api.get("/memory/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `alphacore-memory-${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const runConsolidate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/memory/consolidate");
      toast.success(`Merged ${r.data.merged}, marked ${r.data.outdated} outdated`);
      await loadFacts();
    } catch { toast.error("Consolidation failed"); } finally { setBusy(false); }
  };

  if (!settings) return <div className="p-8 text-white/40 text-sm">Loading…</div>;

  const on = !!settings.memory_enabled;
  const paused = !!settings.paused;

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kr-overline" style={{ color: "rgb(var(--ac-purple))" }}>[ ALPHACORE AI ]</div>
          <h1 className="font-display text-4xl tracking-tight mt-2" data-testid="memory-settings-title">Memory</h1>
          <p className="text-white/55 text-sm mt-1.5">Everything AlphaCore AI remembers about you. You are in control.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportMemory} className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="memory-export-btn">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
          <Button variant="outline" onClick={runConsolidate} disabled={busy || !on} className="border-white/10 hover:bg-white/5 hover:text-white" data-testid="memory-consolidate-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Consolidate
          </Button>
          <Button variant="outline" onClick={() => setConfirmNuke(true)} className="border-red-400/30 text-red-300 hover:bg-red-400/10 hover:text-red-200" data-testid="memory-nuke-btn">
            <Trash2 className="h-4 w-4 mr-2" /> Delete all
          </Button>
        </div>
      </div>

      {/* Master controls */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="kr-card p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="kr-overline">MEMORY</div>
              <div className="font-display text-xl mt-1">{on ? "On" : "Off"}</div>
              <p className="text-xs text-white/50 mt-2 max-w-xs">Turn memory off to stop AlphaCore AI from learning new facts. Existing memories stay until you delete them.</p>
            </div>
            <Switch checked={on} onCheckedChange={(v) => patch({ memory_enabled: v })} disabled={busy} data-testid="memory-master-toggle" />
          </div>
        </div>
        <div className="kr-card p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="kr-overline">EXTRACTION</div>
              <div className="font-display text-xl mt-1 flex items-center gap-2">
                {paused ? <PauseCircle className="h-5 w-5 text-white/50" /> : <PlayCircle className="h-5 w-5" style={{ color: "rgb(var(--ac-green))" }} />}
                {paused ? "Paused" : "Active"}
              </div>
              <p className="text-xs text-white/50 mt-2 max-w-xs">Pause to keep existing memories but stop learning new ones from this session.</p>
            </div>
            <Switch checked={!paused} onCheckedChange={(v) => patch({ paused: !v })} disabled={busy || !on} data-testid="memory-pause-toggle" />
          </div>
        </div>
        <div className="kr-card p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="kr-overline">PERSONALIZATION</div>
              <div className="font-display text-xl mt-1">{settings.personalization ? "On" : "Off"}</div>
              <p className="text-xs text-white/50 mt-2 max-w-xs">Inject relevant memories into AlphaCore AI replies. Toggle off if you want responses to ignore your memory.</p>
            </div>
            <Switch checked={!!settings.personalization} onCheckedChange={(v) => patch({ personalization: v })} disabled={busy || !on} data-testid="memory-personalization-toggle" />
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="mt-6 kr-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="kr-overline">CATEGORIES</div>
            <div className="font-display text-xl mt-1">What we remember</div>
          </div>
          <ShieldCheck className="h-4 w-4 text-white/40" />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
            <label key={k} className="flex items-center justify-between p-3 border border-white/5 hover:bg-white/[0.02] rounded-sm cursor-pointer" data-testid={`memory-cat-${k}`}>
              <div className="text-sm flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: CAT_COLORS[k] }} />
                {label}
              </div>
              <Switch checked={!!settings.categories?.[k]} onCheckedChange={(v) => toggleCategory(k, v)} disabled={busy || !on} />
            </label>
          ))}
        </div>
      </div>

      {/* Facts list */}
      <div className="mt-6 kr-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="kr-overline">MEMORIES</div>
            <div className="font-display text-xl mt-1 flex items-center gap-2">
              <Brain className="h-4 w-4" style={{ color: "rgb(var(--ac-purple))" }} />
              {facts.length} stored
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search memories…" className="pl-8 h-9 w-56 bg-[#050505] border-white/10" data-testid="memory-search" />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 w-44 bg-[#050505] border-white/10" data-testid="memory-cat-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0E] border-white/10">
                <SelectItem value="all">All categories</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-9 w-36 bg-[#050505] border-white/10" data-testid="memory-scope-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0E] border-white/10">
                <SelectItem value="all">All scopes</SelectItem>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="project">Project</SelectItem>
                <SelectItem value="conversation">Conversation</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-5 divide-y divide-white/5" data-testid="memory-list">
          {facts.length === 0 ? (
            <div className="py-14 text-center text-white/40 text-sm">
              {on ? "No memories yet. Chat with AlphaCore AI and it will learn." : "Memory is off. Turn it on above to start learning."}
            </div>
          ) : facts.map((f) => (
            <div key={f.fact_id} className="py-3 flex items-start gap-3" data-testid={`memory-item-${f.fact_id}`}>
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: CAT_COLORS[f.category] || "#888" }} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono uppercase tracking-wider" style={{ color: CAT_COLORS[f.category] || "#aaa" }}>{f.category}</span>
                  <span className="text-xs text-white/30">·</span>
                  <span className="text-xs text-white/40 font-mono">{f.scope}{f.project_id ? ` · ${f.project_id.slice(0, 8)}` : ""}</span>
                  <span className="text-xs text-white/30">·</span>
                  <span className="text-xs text-white/40 font-mono">{Math.round((f.confidence || 0) * 100)}%</span>
                  {f.status !== "active" && <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-white/40 uppercase">{f.status}</span>}
                </div>
                <div className="text-sm mt-1 text-white/85 break-words">{f.value}</div>
                <div className="text-xs text-white/30 mt-0.5 font-mono">{f.key}</div>
              </div>
              <button onClick={() => removeFact(f.fact_id)} className="text-white/30 hover:text-red-300 shrink-0" title="Forget this" data-testid={`memory-delete-${f.fact_id}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Nuke confirmation */}
      <Dialog open={confirmNuke} onOpenChange={setConfirmNuke}>
        <DialogContent className="bg-[#0A0A0E] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Delete all memories?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/60">This permanently erases everything AlphaCore AI has learned about you. Your future conversations will start blank.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmNuke(false)} className="border-white/10 hover:bg-white/5 hover:text-white">Cancel</Button>
            <Button onClick={nukeAll} disabled={busy} className="bg-red-500 hover:bg-red-500/90" data-testid="memory-nuke-confirm">Delete everything</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
